import toast from 'react-hot-toast';

export const MAX_JOB_ATTACHMENT_BYTES = 50 * 1024 * 1024;

export const JOB_ATTACHMENT_ACCEPT =
  '.jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.mp4,.mov,.m4v,.webm,.avi';

export const JOB_ATTACHMENT_HELP_TITLE = 'Allowed files';

export const JOB_ATTACHMENT_HELP_CONTENT =
  'Each file can be up to 50 MB.\n\nPhotos: JPG, PNG, GIF, WebP, BMP\nDocuments: PDF, Word, Excel, TXT\nVideo: MP4, MOV, M4V, WebM, AVI\nArchives: ZIP';

const ALLOWED_EXTENSIONS = new Set(
  JOB_ATTACHMENT_ACCEPT.split(',').map((item) => item.trim().toLowerCase())
);

export const isImageFileName = (fileName: string): boolean =>
  /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(fileName);

export const isVideoFileName = (fileName: string): boolean =>
  /\.(mp4|m4v|mov|webm|avi)$/i.test(fileName);

export const getAttachmentMimeType = (fileName: string): string => {
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  switch (extension) {
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.png':
      return 'image/png';
    case '.gif':
      return 'image/gif';
    case '.webp':
      return 'image/webp';
    case '.bmp':
      return 'image/bmp';
    case '.svg':
      return 'image/svg+xml';
    case '.mp4':
    case '.m4v':
      return 'video/mp4';
    case '.mov':
      return 'video/quicktime';
    case '.webm':
      return 'video/webm';
    case '.avi':
      return 'video/x-msvideo';
    default:
      return 'application/octet-stream';
  }
};

export const createAttachmentObjectUrl = (blob: Blob, fileName: string): string => {
  const mimeType = getAttachmentMimeType(fileName);
  const typedBlob =
    blob.type && blob.type !== 'application/octet-stream'
      ? blob
      : new Blob([blob], { type: mimeType });
  return window.URL.createObjectURL(typedBlob);
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message || err?.message || fallback;
};

export const takeAllowedAttachments = (files: File[]): File[] => {
  const accepted: File[] = [];
  const rejected: string[] = [];

  files.forEach((file) => {
    const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (!ALLOWED_EXTENSIONS.has(extension)) {
      rejected.push(`${file.name}: file type is not allowed`);
      return;
    }
    if (file.size > MAX_JOB_ATTACHMENT_BYTES) {
      rejected.push(`${file.name}: exceeds the 50 MB limit`);
      return;
    }
    accepted.push(file);
  });

  if (rejected.length > 0) {
    toast.error(rejected.join('. '));
  }

  return accepted;
};
